import React, { useEffect, useState } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import uploadBtn from "../assets/uploadBtn.svg";
import arrDown from "../assets/arrowDown.svg";
// import DatePosted from "./DatePosted";
import axiosInstance from "../axios/axios";
// import FilterByCompany from "./FilterByCompany";
// import FilterByField from "./FilterByField";
// import FilterByStipend from "./FilterByStipend";
// import FilterByLocation from "./FilterByLocation";
import CompanyJobs from "./CompanyJobs";
import Loader from "./Loader";

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [error, setError] = useState("");
  const [dropdownStates, setDropdownStates] = useState(null);
  const [companyNames, setCompanyNames] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();

  const [filterStates, setFilterStates] = useState({
    datePosted: false,
    company: false,
    field: false,
    stipend: false,
    location: false,
  });

  const [selectedFilters, setSelectedFilters] = useState({
    datePosted: "",
    company: "",
    field: "",
    stipend: "",
    location: "",
  });

  // Define date options for the dropdown
  const dateOptions = [
    "Today",
    "Yesterday",
    // "Last 3 days",
    "Last week",
    // "Last month",
    "All time",
  ];

  const filterItem = [
    {
      id: 1,
      name: "Date",
      stateKey: "datePosted",
      img: arrDown,
      dropdown: dateOptions,
    },
    {
      id: 2,
      name: "Company",
      stateKey: "company",
      img: arrDown,
      dropdown: companyNames,
    },
    {
      id: 3,
      name: "Field",
      stateKey: "field",
      img: arrDown,
      dropdown: ["Art", "Commercial", "Science"],
    },
    {
      id: 4,
      name: "Stipend",
      stateKey: "stipend",
      img: arrDown,
      dropdown: ["Yes", "No"],
    },
    {
      id: 5,
      name: "Location",
      stateKey: "location",
      img: arrDown,
      dropdown: locations,
    },
  ];

  const userRole = localStorage.getItem("userRole");

  // Toggle dropdown while ensuring only one is open at a time
  const handleDropdownToggle = (dropdownId) => {
    setDropdownStates((prev) => (prev === dropdownId ? null : dropdownId));
  };

  const handleDropdownItemClick = (dropdownItem, stateKey) => {
    // Update selected filters
    setSelectedFilters((prev) => ({
      ...prev,
      [stateKey]: dropdownItem,
    }));

    // Set filter state to active
    setFilterStates((prev) => ({
      ...prev,
      [stateKey]: true,
    }));

    console.log(`Selected ${stateKey}:`, dropdownItem);
    setDropdownStates(null); // Close all dropdowns after selection
  };

  const handleItemClick = (stateKey) => {
    setFilterStates((prev) =>
      Object.keys(prev).reduce((acc, key) => {
        acc[key] = key === stateKey ? !prev[stateKey] : false;
        return acc;
      }, {})
    );

    setDropdownStates(null); // Close dropdown when clicking filter button
  };

  // Clear filter function
  const clearFilter = (stateKey) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [stateKey]: "",
    }));

    setFilterStates((prev) => ({
      ...prev,
      [stateKey]: false,
    }));
  };

  // Filter jobs based on selected filters
  const applyFilters = () => {
    let filtered = [...jobs];

    // Filter by date
    if (selectedFilters.datePosted) {
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      filtered = filtered.filter((job) => {
        const jobDate = new Date(job.created_at);
        jobDate.setHours(0, 0, 0, 0);

        const diffDays = Math.floor(
          (currentDate - jobDate) / (1000 * 60 * 60 * 24)
        );

        switch (selectedFilters.datePosted) {
          case "Today":
            return diffDays === 0;
          case "Yesterday":
            return diffDays === 1;
          case "Last 3 days":
            return diffDays <= 3;
          case "Last week":
            return diffDays <= 7;
          case "Last month":
            return diffDays <= 30;
          case "All time":
            return true;
          default:
            return true;
        }
      });
    }

    // Filter by company
    if (selectedFilters.company) {
      filtered = filtered.filter((job) =>
        job.company_name
          ?.toLowerCase()
          .includes(selectedFilters.company.toLowerCase())
      );
    }

    // Filter by field
    if (selectedFilters.field) {
      filtered = filtered.filter(
        (job) =>
          job.field
            ?.toLowerCase()
            .includes(selectedFilters.field.toLowerCase()) ||
          job.job_title
            ?.toLowerCase()
            .includes(selectedFilters.field.toLowerCase())
      );
    }

    // Filter by stipend
    if (selectedFilters.stipend) {
      const hasStipend = selectedFilters.stipend === "Yes";
      filtered = filtered.filter((job) =>
        hasStipend ? job.stipend === 1 : job.stipend === 0
      );
    }

    // Filter by location
    if (selectedFilters.location) {
      filtered = filtered.filter((job) =>
        job.location
          ?.toLowerCase()
          .includes(selectedFilters.location.toLowerCase())
      );
    }

    setFilteredJobs(filtered);
  };

  // Apply filters whenever selectedFilters or jobs change
  useEffect(() => {
    applyFilters();
  }, [selectedFilters, jobs]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/jobs`);
        const allJobs = response.data.jobs.data || [];

        // Get the current date (normalized to midnight to avoid time discrepancies)
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        // Filter jobs where the deadline is in the future or today AND status is not 0
        const activeJobs = allJobs.filter((job) => {
          const jobDeadline = new Date(job.deadline);
          jobDeadline.setHours(0, 0, 0, 0); // Normalize time

          return jobDeadline >= currentDate && job.status !== 0;
        });

        setJobs(activeJobs);
        setFilteredJobs(activeJobs); // Initialize filtered jobs
        console.log(activeJobs);
        setError("");
      } catch {
        setLoading(false);
        setError("Failed to fetch jobs. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchCompanyNames = async () => {
      try {
        const response = await axiosInstance.get("company_names");
        setCompanyNames(response.data.data);
      } catch {
        console.log("Error fetching company names");
      }
    };
    fetchCompanyNames();
  }, []);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axiosInstance.get("locations");
        setLocations(response.data.data.map((state) => state.name));
      } catch {
        console.log("Error fetching locations");
      }
    };
    fetchLocations();
  }, []);

  const truncateText = (text, maxLength) => {
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  if (loading) return <Loader />;

  return id ? (
    <Outlet />
  ) : (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="flex justify-between items-center px-4 sm:px-6 py-4">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Jobs</h1>
          {userRole === "organization" && (
            <Link
              to="/dashboard/create-job"
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 text-sm font-medium"
            >
              <img src={uploadBtn} alt="Upload" className="w-4 h-4" />
              <span className="hidden sm:inline">Create Job</span>
              <span className="sm:hidden">Create</span>
            </Link>
          )}
        </div>
      </div>

      {userRole === "organization" ? (
        <CompanyJobs />
      ) : (
        <div className="flex-1">
          {/* Filter Section */}
          <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 relative z-30 overflow-visible">
            <div className="flex gap-2 overflow-x-auto overflow-y-visible pb-2 scrollbar-hide">
              {filterItem.map((item) => (
                <div key={item.id} className="relative flex-shrink-0 z-10">
                  <div
                      data-dropdown-trigger={item.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap text-sm font-medium transition-colors duration-200 ${
                        filterStates[item.stateKey]
                          ? "bg-green-50 border-2 border-green-500 text-green-700"
                          : "bg-gray-100 border-2 border-transparent text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                    <span onClick={() => handleItemClick(item.stateKey)}>
                      {selectedFilters[item.stateKey] || item.name}
                    </span>

                    {/* Clear filter button */}
                    {selectedFilters[item.stateKey] && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          clearFilter(item.stateKey);
                        }}
                        className="ml-1 text-red-500 hover:text-red-700 font-bold text-lg leading-none"
                        title="Clear filter"
                      >
                        ×
                      </button>
                    )}

                    {item.img && (
                      <img
                        src={item.img}
                        className="w-4 h-4 cursor-pointer hover:bg-green-200 rounded p-0.5 transition-colors"
                        alt="Toggle dropdown"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDropdownToggle(item.id);
                        }}
                      />
                    )}
                  </div>

{dropdownStates === item.id && item.dropdown.length > 0 && (
  <>
    {/* Mobile backdrop and dropdown */}
    <div className="md:hidden">
      <div 
        className="fixed inset-0 bg-black bg-opacity-25 z-[1000]"
        onClick={() => setDropdownStates(null)}
      />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-[1001] max-h-[70vh] overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Select {item.name}
            </h3>
            <button
              onClick={() => setDropdownStates(null)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[calc(70vh-80px)] p-2">
          {item.dropdown.map((dropdownItem, index) => (
            <div
              key={index}
              className={`px-4 py-4 cursor-pointer text-base transition-colors rounded-lg mb-1 ${
                selectedFilters[item.stateKey] === dropdownItem
                  ? "bg-green-50 text-green-700 font-medium border border-green-200"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
              onClick={() =>
                handleDropdownItemClick(dropdownItem, item.stateKey)
              }
            >
              {dropdownItem}
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Desktop dropdown - using fixed positioning */}
    <div 
      className="hidden md:block fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[1000] min-w-[220px] max-w-[300px]"
      style={{
        top: `${document.querySelector(`[data-dropdown-trigger="${item.id}"]`)?.getBoundingClientRect()?.bottom + window.scrollY + 8}px`,
        left: `${document.querySelector(`[data-dropdown-trigger="${item.id}"]`)?.getBoundingClientRect()?.left}px`
      }}
    >
      <div className="py-2 max-h-64 overflow-y-auto">
        {item.dropdown.map((dropdownItem, index) => (
          <div
            key={index}
            className={`px-4 py-3 cursor-pointer text-sm transition-colors border-b border-gray-50 last:border-b-0 ${
              selectedFilters[item.stateKey] === dropdownItem
                ? "bg-green-50 text-green-700 font-medium"
                : "text-gray-700 hover:bg-gray-50"
            }`}
            onClick={() =>
              handleDropdownItemClick(dropdownItem, item.stateKey)
            }
          >
            {dropdownItem}
          </div>
        ))}
      </div>
    </div>
  </>
)}
                </div>
              ))}
            </div>
          </div>
          {/* Active Filters Display */}
          {Object.values(selectedFilters).some((filter) => filter) && (
            <div className="bg-blue-50 border-b border-blue-200 px-4 sm:px-6 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-blue-800">Active filters:</span>
                {Object.entries(selectedFilters).map(
                  ([key, value]) =>
                    value && (
                      <span
                        key={key}
                        className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium"
                      >
                        {key}: {value}
                      </span>
                    )
                )}
                <button
                  onClick={() => {
                    setSelectedFilters({
                      datePosted: "",
                      company: "",
                      field: "",
                      stipend: "",
                      location: "",
                    });
                    setFilterStates({
                      datePosted: false,
                      company: false,
                      field: false,
                      stipend: false,
                      location: false,
                    });
                  }}
                  className="text-red-600 hover:text-red-800 text-xs font-medium underline"
                >
                  Clear all
                </button>
              </div>
            </div>
          )}

          {/* Jobs Display Section */}
          <div className="px-4 sm:px-6 py-6">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredJobs.length > 0 ? (
                filteredJobs.map((job) => {
                  const createdAt = new Date(job.created_at);
                  const diffDays = Math.ceil(
                    (new Date() - createdAt) / (1000 * 60 * 60 * 24)
                  );

                  const userId = localStorage.getItem("user_id");
                  const hasApplied = job.application?.some(
                    (applicant) => String(applicant) === String(userId)
                  );

                  return (
                    <div
                      key={job.id}
                      className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow duration-300 flex flex-col"
                    >
                      {/* Header with title and status badges */}
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1 min-w-0 pr-4">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {job.job_title || "Untitled Job"}
                          </h3>
                          <p className="text-sm text-gray-600 truncate">
                            {job.company_name || "Unknown Company"}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {/* Stipend */}
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            job.stipend === 1 
                              ? "bg-green-100 text-green-800" 
                              : "bg-gray-100 text-gray-600"
                          }`}>
                            {job.stipend === 1 ? `₦${job.amount}` : "Unpaid"}
                          </span>
                          {/* Application status */}
                          {hasApplied && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Applied
                            </span>
                          )}
                        </div>
                      </div>

                     {/* Job Meta: Location, Duration, Mode */}
                    <div className="flex flex-wrap gap-3 mb-2">
                      {job.location && (
                        <span className="inline-flex items-center text-xs text-gray-500">
                          📍 {job.location}
                        </span>
                      )}
                      {job.duration && (
                        <span className="inline-flex items-center text-xs text-gray-500">
                          ⏳ {job.duration}
                        </span>
                      )}
                      {job.env_mode && (
                        <span className="inline-flex items-center text-xs text-gray-500">
                          💼 {job.env_mode}
                        </span>
                      )}
                    </div>

                      {/* Job description */}
                      <div className="mb-4 flex-1">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {truncateText(job.job_description, 120)}
                        </p>
                      </div>

                      {job.deadline && (
                        <span className="inline-flex items-center text-xs text-gray-500">
                          Deadline: {job.deadline}
                        </span>
                      )}

                      {/* Footer with date and action button */}
                      <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                        <span className="text-xs text-gray-500">
                          {diffDays === 0
                            ? "Today"
                            : diffDays === 1
                            ? "Yesterday"
                            : diffDays > 1
                            ? `${diffDays} days ago`
                            : `In ${Math.abs(diffDays)} days`}
                        </span>
                        <Link
                          to={job.id ? `${job.id}` : "/job/unknown"}
                          className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors duration-200"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full">
                  <div className="text-center py-12">
                    <div className="mb-4">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0H8m8 0v2a2 2 0 002 2M8 6V4m0 2v2a2 2 0 002 2m0 0h4m-4 0a2 2 0 00-2 2v4a2 2 0 002 2h4a2 2 0 002-2v-4a2 2 0 00-2-2"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {Object.values(selectedFilters).some((filter) => filter)
                        ? "No jobs match your filters"
                        : "No jobs available"}
                    </h3>
                    <p className="text-gray-500 mb-4">
                      {Object.values(selectedFilters).some((filter) => filter)
                        ? "Try adjusting your filters to see more results."
                        : "Check back later for new opportunities."}
                    </p>
                    {Object.values(selectedFilters).some((filter) => filter) && (
                      <button
                        onClick={() => {
                          setSelectedFilters({
                            datePosted: "",
                            company: "",
                            field: "",
                            stipend: "",
                            location: "",
                          });
                          setFilterStates({
                            datePosted: false,
                            company: false,
                            field: false,
                            stipend: false,
                            location: false,
                          });
                        }}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;